import {
    Anchor,
    Badge,
    Button,
    Group,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    AttractionFactCandidateRO,
    AttractionFactDecisionEnum,
    AttractionFactKindEnum,
    AttractionFactRoleEnum,
    AttractionFactTruthEnum,
    AttractionFactVerdictEnum,
    AttractionResearchDepthEnum,
    AttractionResearchRunRO,
    AttractionResearchRunStatusEnum,
    UpdateAttractionFactDto,
} from '@routellers/types/dist/city-attraction/attraction-fact';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';
import { Section } from '../../components/editor';

type View = 'all' | 'passed' | 'pending' | 'chosen' | 'rejected' | 'duplicates';
type Depth = 'auto' | AttractionResearchDepthEnum;
type Job = 'research' | 'deepen' | 'dedupe' | 'score';

function tokens(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `${Math.round(value / 1_000)}k`;

    return String(value);
}

const TRUTH_COLOR: Record<AttractionFactTruthEnum, string> = {
    [AttractionFactTruthEnum.UNCHECKED]: 'gray',
    [AttractionFactTruthEnum.CONFIRMED]: 'green',
    [AttractionFactTruthEnum.LEGEND]: 'grape',
    [AttractionFactTruthEnum.DISPUTED]: 'orange',
    [AttractionFactTruthEnum.UNVERIFIED]: 'yellow',
    [AttractionFactTruthEnum.FALSE]: 'red',
};

const VERDICT_COLOR: Record<AttractionFactVerdictEnum, string> = {
    [AttractionFactVerdictEnum.PASSED]: 'green',
    [AttractionFactVerdictEnum.NEEDS_CHECK]: 'yellow',
    [AttractionFactVerdictEnum.FAILED]: 'red',
    [AttractionFactVerdictEnum.DUPLICATE]: 'gray',
};

const DECISION_COLOR: Record<AttractionFactDecisionEnum, string> = {
    [AttractionFactDecisionEnum.PENDING]: 'gray',
    [AttractionFactDecisionEnum.TAKE]: 'blue',
    [AttractionFactDecisionEnum.MUST]: 'teal',
    [AttractionFactDecisionEnum.REJECT]: 'red',
};

const ACTIVE_RUN = [
    AttractionResearchRunStatusEnum.QUEUED,
    AttractionResearchRunStatusEnum.RUNNING,
];

function ratingColor(rating: number | null): string {
    if (rating == null) return 'gray';
    if (rating >= 80) return 'teal';
    if (rating >= 60) return 'blue';
    if (rating >= 40) return 'yellow';

    return 'red';
}

export function isChosen(fact: AttractionFactCandidateRO): boolean {
    return (
        fact.editorDecision === AttractionFactDecisionEnum.TAKE ||
        fact.editorDecision === AttractionFactDecisionEnum.MUST
    );
}

const FactCard = ({
    fact,
    onUpdate,
    onDeepen,
    deepenDisabled,
}: {
    fact: AttractionFactCandidateRO;
    onUpdate: (fact: AttractionFactCandidateRO, patch: UpdateAttractionFactDto) => void;
    onDeepen: (fact: AttractionFactCandidateRO) => void;
    deepenDisabled: boolean;
}) => {
    const { t } = useTranslation();
    const [showSources, setShowSources] = useState(false);
    const [note, setNote] = useState(fact.editorNote || '');
    const grounded = fact.sources.filter((s) => s.grounded === true).length;
    const scores = fact.aiScores
        ? Object.entries(fact.aiScores)
              .map(([axis, value]) => `${t(`editors.attractionFacts.score.${axis}`)} ${value}`)
              .join(' · ')
        : null;

    useEffect(() => setNote(fact.editorNote || ''), [fact.editorNote]);

    return (
        <Paper
            withBorder
            p='sm'
            radius='md'
            style={{
                borderLeft: `4px solid var(--mantine-color-${DECISION_COLOR[fact.editorDecision]}-5)`,
                opacity: fact.editorDecision === AttractionFactDecisionEnum.REJECT ? 0.6 : 1,
            }}
        >
            <Stack gap={6}>
                <Group gap='xs' wrap='nowrap' align='flex-start'>
                    <Badge color={ratingColor(fact.aiRating)} variant='filled' miw={44}>
                        {fact.aiRating ?? '—'}
                    </Badge>
                    <Stack gap={2} style={{ flex: 1 }}>
                        <Text size='sm' fw={600}>
                            {fact.claim}
                            {fact.year ? (
                                <Text span size='xs' c='dimmed' fw={400}>
                                    {'  '}
                                    {fact.year}
                                </Text>
                            ) : null}
                        </Text>
                        {fact.detail && (
                            <Text size='sm' c='dimmed'>
                                {fact.detail}
                            </Text>
                        )}
                    </Stack>
                    <Text size='xs' c='dimmed'>
                        #{fact.id}
                    </Text>
                </Group>

                <Group gap={6}>
                    <Badge variant='light' color='gray'>
                        {t(`editors.attractionFacts.kind.${fact.kind}`)}
                    </Badge>
                    <Badge variant='light' color={TRUTH_COLOR[fact.truthStatus]}>
                        {t(`editors.attractionFacts.truth.${fact.truthStatus}`)}
                    </Badge>
                    {fact.aiVerdict && (
                        <Badge variant='outline' color={VERDICT_COLOR[fact.aiVerdict]}>
                            {t('editors.attractionFacts.aiPrefix')}{' '}
                            {t(`editors.attractionFacts.verdict.${fact.aiVerdict}`)}
                        </Badge>
                    )}
                    {fact.aiSuggestedRole && (
                        <Badge variant='outline' color='violet'>
                            {t('editors.attractionFacts.aiPrefix')}{' '}
                            {t(`editors.attractionFacts.role.${fact.aiSuggestedRole}`)}
                        </Badge>
                    )}
                    {fact.aiReviewer?.startsWith('draft') && (
                        <Badge variant='dot' color='gray'>
                            {t('editors.attractionFacts.draftBadge')}
                        </Badge>
                    )}
                    {fact.duplicateOfId && (
                        <Badge variant='light' color='gray'>
                            {t('editors.attractionFacts.duplicateOf', { id: fact.duplicateOfId })}
                        </Badge>
                    )}
                    {fact.deepenedAt && (
                        <Badge variant='light' color='teal'>
                            {t('editors.attractionFacts.deepened')}
                        </Badge>
                    )}
                    {fact.observable && (
                        <Badge variant='light' color='cyan'>
                            {t('editors.attractionFacts.observable')}
                        </Badge>
                    )}
                    {fact.sensitivity !== 'none' && (
                        <Badge variant='light' color='pink'>
                            {t(`editors.attractionFacts.sensitivity.${fact.sensitivity}`)}
                        </Badge>
                    )}
                    {fact.volatile && (
                        <Badge variant='light' color='orange'>
                            {t('editors.attractionFacts.volatile')}
                        </Badge>
                    )}
                    <UnstyledButton onClick={() => setShowSources((v) => !v)}>
                        <Badge
                            variant='dot'
                            color={
                                grounded ? 'green' : fact.sourceGrounded === false ? 'red' : 'gray'
                            }
                            style={{ cursor: 'pointer' }}
                        >
                            {t('editors.attractionFacts.sources', {
                                count: fact.sources.length,
                                grounded,
                            })}
                        </Badge>
                    </UnstyledButton>
                    {fact.usedIn.length > 0 && (
                        <Badge variant='light' color='indigo'>
                            {t('editors.attractionFacts.usedIn', { list: fact.usedIn.join(', ') })}
                        </Badge>
                    )}
                </Group>

                {fact.whereToSee && (
                    <Text size='xs'>
                        {t('editors.attractionFacts.whereToSee')}: {fact.whereToSee}
                    </Text>
                )}
                {(fact.aiNotes || scores) && (
                    <Text size='xs' c='dimmed' fs='italic'>
                        {fact.aiNotes}
                        {scores ? ` (${scores})` : ''}
                    </Text>
                )}

                {showSources && (
                    <Stack
                        gap={6}
                        pl='sm'
                        style={{ borderLeft: '2px solid var(--mantine-color-gray-3)' }}
                    >
                        {fact.sources.map((source, index) => (
                            <Stack key={`${source.url}-${index}`} gap={0}>
                                <Group gap={6}>
                                    <Text
                                        size='xs'
                                        c={
                                            source.grounded
                                                ? 'green'
                                                : source.grounded === false
                                                  ? 'red'
                                                  : 'dimmed'
                                        }
                                    >
                                        {source.grounded
                                            ? t('editors.attractionFacts.groundedYes')
                                            : source.grounded === false
                                              ? t('editors.attractionFacts.groundedNo')
                                              : t('editors.attractionFacts.groundedUnknown')}
                                    </Text>
                                    {source.url ? (
                                        <Anchor href={source.url} target='_blank' size='xs'>
                                            {source.title || source.publisher || source.url}
                                        </Anchor>
                                    ) : (
                                        <Text size='xs'>{source.title || source.publisher}</Text>
                                    )}
                                </Group>
                                {source.quote && (
                                    <Text size='xs' c='dimmed'>
                                        «{source.quote}»
                                    </Text>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                )}

                <Group gap='xs' align='flex-end'>
                    <SegmentedControl
                        size='xs'
                        value={fact.editorDecision}
                        color={DECISION_COLOR[fact.editorDecision]}
                        onChange={(value) =>
                            onUpdate(fact, { editorDecision: value as AttractionFactDecisionEnum })
                        }
                        data={Object.values(AttractionFactDecisionEnum).map((value) => ({
                            value,
                            label: t(`editors.attractionFacts.decision.${value}`),
                        }))}
                    />
                    <Select
                        size='xs'
                        w={170}
                        clearable
                        placeholder={t('editors.attractionFacts.rolePlaceholder')}
                        value={fact.editorRole}
                        onChange={(value) =>
                            onUpdate(fact, {
                                editorRole: (value as AttractionFactRoleEnum) || null,
                            })
                        }
                        data={Object.values(AttractionFactRoleEnum).map((value) => ({
                            value,
                            label: t(`editors.attractionFacts.role.${value}`),
                        }))}
                    />
                    <TextInput
                        size='xs'
                        style={{ flex: 1, minWidth: 180 }}
                        placeholder={t('editors.attractionFacts.notePlaceholder')}
                        value={note}
                        onChange={(event) => setNote(event.currentTarget.value)}
                        onBlur={() => {
                            if (note !== (fact.editorNote || ''))
                                onUpdate(fact, { editorNote: note });
                        }}
                    />
                    {isChosen(fact) && (
                        <Button
                            size='xs'
                            variant='light'
                            disabled={deepenDisabled}
                            onClick={() => onDeepen(fact)}
                        >
                            {t('editors.attractionFacts.deepenOne')}
                        </Button>
                    )}
                </Group>
            </Stack>
        </Paper>
    );
};

const NewFactForm = ({
    attractionId,
    onCreated,
}: {
    attractionId: number;
    onCreated: () => void;
}) => {
    const { t } = useTranslation();
    const [claim, setClaim] = useState('');
    const [detail, setDetail] = useState('');
    const [url, setUrl] = useState('');
    const [quote, setQuote] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setSaving(true);
        try {
            await api.cityAttractionsApi.createFact({
                id: attractionId,
                data: {
                    claim,
                    detail: detail || null,
                    sources: url || quote ? [{ url: url || null, quote: quote || null }] : [],
                },
            });
            setClaim('');
            setDetail('');
            setUrl('');
            setQuote('');
            onCreated();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper withBorder p='sm' radius='md'>
            <Stack gap='xs'>
                <Textarea
                    label={t('editors.attractionFacts.newClaim')}
                    autosize
                    minRows={2}
                    value={claim}
                    onChange={(e) => setClaim(e.currentTarget.value)}
                />
                <Textarea
                    label={t('editors.attractionFacts.newDetail')}
                    autosize
                    minRows={1}
                    value={detail}
                    onChange={(e) => setDetail(e.currentTarget.value)}
                />
                <Group grow>
                    <TextInput
                        label={t('editors.attractionFacts.newUrl')}
                        value={url}
                        onChange={(e) => setUrl(e.currentTarget.value)}
                    />
                    <TextInput
                        label={t('editors.attractionFacts.newQuote')}
                        value={quote}
                        onChange={(e) => setQuote(e.currentTarget.value)}
                    />
                </Group>
                <Group justify='flex-end'>
                    <Button size='xs' loading={saving} disabled={!claim.trim()} onClick={submit}>
                        {t('editors.attractionFacts.addFact')}
                    </Button>
                </Group>
            </Stack>
        </Paper>
    );
};

export const FactsPanel = ({
    attractionId,
    onChanged,
}: {
    attractionId: number;
    onChanged?: () => void | Promise<void>;
}) => {
    const { t } = useTranslation();
    const [facts, setFacts] = useState<AttractionFactCandidateRO[]>([]);
    const [runs, setRuns] = useState<AttractionResearchRunRO[]>([]);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState<Job | null>(null);
    const [depth, setDepth] = useState<Depth>('auto');
    const [view, setView] = useState<View>('all');
    const [kind, setKind] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [adding, setAdding] = useState(false);
    const [showLog, setShowLog] = useState(false);

    const loadFacts = useCallback(async () => {
        const { data } = await api.cityAttractionsApi.listFacts(attractionId);
        setFacts(data);
    }, [attractionId]);

    const loadRuns = useCallback(async () => {
        const { data } = await api.cityAttractionsApi.factResearchRuns(attractionId);
        setRuns(data);

        return data;
    }, [attractionId]);

    useEffect(() => {
        setLoading(true);
        Promise.all([loadFacts(), loadRuns()])
            .catch((error) => notifications.show({ color: 'red', message: apiErrorMessage(error) }))
            .finally(() => setLoading(false));
    }, [loadFacts, loadRuns]);

    const latest = runs[0];
    const running = !!latest && ACTIVE_RUN.includes(latest.status);

    useEffect(() => {
        if (!running) return;
        const timer = setInterval(async () => {
            try {
                const next = await loadRuns();
                if (!next[0] || !ACTIVE_RUN.includes(next[0].status)) {
                    await loadFacts();
                    onChanged?.();
                }
            } catch {
                /* keep polling */
            }
        }, 5000);

        return () => clearInterval(timer);
    }, [running, loadRuns, loadFacts, onChanged]);

    const startJob = async (job: Job, factIds?: number[]) => {
        setStarting(job);
        try {
            const { data } =
                job === 'research'
                    ? await api.cityAttractionsApi.startFactResearch({
                          id: attractionId,
                          data: depth === 'auto' ? {} : { depth },
                      })
                    : job === 'deepen'
                      ? await api.cityAttractionsApi.deepenFacts({
                            id: attractionId,
                            data: factIds ? { factIds } : {},
                        })
                      : job === 'dedupe'
                        ? await api.cityAttractionsApi.dedupeFacts(attractionId)
                        : await api.cityAttractionsApi.draftScoreFacts(attractionId);
            notifications.show({
                color: 'green',
                message: t('editors.attractionFacts.runStarted', { id: data.id }),
            });
            await loadRuns();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setStarting(null);
        }
    };

    const update = async (fact: AttractionFactCandidateRO, patch: UpdateAttractionFactDto) => {
        setFacts((list) =>
            list.map((f) =>
                f.id === fact.id ? ({ ...f, ...patch } as AttractionFactCandidateRO) : f,
            ),
        );
        try {
            const { data } = await api.cityAttractionsApi.updateFact({
                factId: fact.id,
                data: patch,
            });
            setFacts((list) => list.map((f) => (f.id === fact.id ? data : f)));
        } catch (error) {
            setFacts((list) => list.map((f) => (f.id === fact.id ? fact : f)));
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    const counts = useMemo(() => {
        const active = facts.filter((f) => !f.duplicateOfId);

        return {
            all: active.length,
            passed: active.filter((f) => f.aiVerdict === AttractionFactVerdictEnum.PASSED).length,
            pending: active.filter((f) => f.editorDecision === AttractionFactDecisionEnum.PENDING)
                .length,
            chosen: active.filter(isChosen).length,
            rejected: active.filter((f) => f.editorDecision === AttractionFactDecisionEnum.REJECT)
                .length,
            duplicates: facts.length - active.length,
        };
    }, [facts]);

    const visible = useMemo(() => {
        const query = search.trim().toLowerCase();

        return facts.filter((fact) => {
            if ((view === 'duplicates') !== !!fact.duplicateOfId) return false;
            if (view === 'passed' && fact.aiVerdict !== AttractionFactVerdictEnum.PASSED)
                return false;
            if (view === 'pending' && fact.editorDecision !== AttractionFactDecisionEnum.PENDING)
                return false;
            if (view === 'chosen' && !isChosen(fact)) return false;
            if (view === 'rejected' && fact.editorDecision !== AttractionFactDecisionEnum.REJECT)
                return false;
            if (kind && fact.kind !== kind) return false;
            if (query && !`${fact.claim} ${fact.detail || ''}`.toLowerCase().includes(query))
                return false;

            return true;
        });
    }, [facts, view, kind, search]);

    return (
        <Stack gap='md'>
            <Section
                boxed
                title={t('editors.attractionFacts.title')}
                description={t('editors.attractionFacts.description')}
            >
                <Group justify='space-between' align='flex-start'>
                    <Stack gap={2}>
                        {latest ? (
                            <>
                                <Text size='sm'>
                                    {t('editors.attractionFacts.runLine', {
                                        id: latest.id,
                                        kind: t(
                                            `editors.attractionFacts.runKind.${latest.kind || 'research'}`,
                                        ),
                                        status: t(
                                            `editors.attractionFacts.runStatus.${latest.status}`,
                                        ),
                                        found: latest.found,
                                        created: latest.created,
                                        duplicates: latest.duplicates,
                                        grounded: latest.grounded,
                                        scored: latest.scored ?? 0,
                                    })}
                                </Text>
                                {latest.usage && (
                                    <Text size='xs' c='dimmed'>
                                        {t('editors.attractionFacts.usageLine', {
                                            input: tokens(latest.usage.total.inputTokens),
                                            cached: tokens(latest.usage.total.cachedTokens),
                                            output: tokens(latest.usage.total.outputTokens),
                                            reasoning: tokens(latest.usage.total.reasoningTokens),
                                            searches: latest.usage.total.webSearches,
                                            calls: latest.usage.total.calls,
                                        })}
                                    </Text>
                                )}
                                {latest.error && (
                                    <Text size='xs' c='red'>
                                        {latest.error}
                                    </Text>
                                )}
                                <Anchor
                                    size='xs'
                                    component='button'
                                    onClick={() => setShowLog((v) => !v)}
                                >
                                    {showLog
                                        ? t('editors.attractionFacts.hideLog')
                                        : t('editors.attractionFacts.showLog')}
                                </Anchor>
                            </>
                        ) : (
                            <Text size='sm' c='dimmed'>
                                {t('editors.attractionFacts.noRuns')}
                            </Text>
                        )}
                    </Stack>
                    <Group gap='xs' justify='flex-end'>
                        <Button variant='light' size='xs' onClick={() => setAdding((v) => !v)}>
                            {t('editors.attractionFacts.addFact')}
                        </Button>
                        <Button
                            variant='light'
                            size='xs'
                            disabled={running || !counts.chosen}
                            loading={starting === 'deepen'}
                            onClick={() => void startJob('deepen')}
                        >
                            {t('editors.attractionFacts.deepenChosen')}
                        </Button>
                        <Button
                            variant='light'
                            size='xs'
                            disabled={running || !facts.length}
                            loading={starting === 'dedupe'}
                            onClick={() => void startJob('dedupe')}
                        >
                            {t('editors.attractionFacts.dedupe')}
                        </Button>
                        <Button
                            variant='light'
                            size='xs'
                            disabled={running || !facts.length}
                            loading={starting === 'score'}
                            onClick={() => void startJob('score')}
                        >
                            {t('editors.attractionFacts.draftScore')}
                        </Button>
                        <Select
                            size='xs'
                            w={170}
                            value={depth}
                            allowDeselect={false}
                            onChange={(value) => setDepth((value as Depth) || 'auto')}
                            data={(
                                ['auto', ...Object.values(AttractionResearchDepthEnum)] as Depth[]
                            ).map((value) => ({
                                value,
                                label: t(`editors.attractionFacts.depth.${value}`),
                            }))}
                        />
                        <Button
                            size='xs'
                            loading={starting === 'research' || running}
                            onClick={() => void startJob('research')}
                        >
                            {t('editors.attractionFacts.startResearch')}
                        </Button>
                    </Group>
                </Group>
                {showLog && latest && (
                    <Stack gap={0}>
                        {latest.log.map((entry, index) => (
                            <Text key={index} size='xs' c='dimmed'>
                                {new Date(entry.at).toLocaleTimeString()} — {entry.message}
                            </Text>
                        ))}
                    </Stack>
                )}
            </Section>

            {adding && (
                <NewFactForm
                    attractionId={attractionId}
                    onCreated={() => {
                        setAdding(false);
                        void loadFacts();
                        onChanged?.();
                    }}
                />
            )}

            <Group gap='sm' align='flex-end'>
                <SegmentedControl
                    size='xs'
                    value={view}
                    onChange={(value) => setView(value as View)}
                    data={(
                        ['all', 'passed', 'pending', 'chosen', 'rejected', 'duplicates'] as View[]
                    ).map((value) => ({
                        value,
                        label: `${t(`editors.attractionFacts.view.${value}`)} ${counts[value]}`,
                    }))}
                />
                <Select
                    size='xs'
                    w={180}
                    clearable
                    placeholder={t('editors.attractionFacts.kindPlaceholder')}
                    value={kind}
                    onChange={(value) => setKind(value ? String(value) : null)}
                    data={Object.values(AttractionFactKindEnum).map((value) => ({
                        value,
                        label: t(`editors.attractionFacts.kind.${value}`),
                    }))}
                />
                <TextInput
                    size='xs'
                    style={{ flex: 1 }}
                    placeholder={t('editors.attractionFacts.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                />
            </Group>

            {loading ? (
                <Text size='sm' c='dimmed'>
                    {t('editors.attractionFacts.loading')}
                </Text>
            ) : visible.length === 0 ? (
                <Text size='sm' c='dimmed'>
                    {t('editors.attractionFacts.empty')}
                </Text>
            ) : (
                <Stack gap='xs' pb={40}>
                    {visible.map((fact) => (
                        <FactCard
                            key={fact.id}
                            fact={fact}
                            onUpdate={update}
                            onDeepen={(target) => void startJob('deepen', [target.id])}
                            deepenDisabled={running || !!starting}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
